import { useEffect, useState } from 'react'
import { X, Trophy, UserPlus, Check, Flame } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/Button'
import {
  getLeaderboard, getFriendRequests, inviteFriend, acceptFriend,
  type LeaderboardEntry, type FriendRequest,
} from '@/api/friends'

export function LeaderboardPage({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [requests, setRequests] = useState<FriendRequest[]>([])
  const [email, setEmail] = useState('')
  const [inviteMsg, setInviteMsg] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = () => {
    Promise.all([getLeaderboard(userId), getFriendRequests(userId)]).then(([lb, reqs]) => {
      setEntries(lb); setRequests(reqs); setLoading(false)
    })
  }
  useEffect(refresh, [userId])

  const submitInvite = async () => {
    if (!email.trim()) return
    const res = await inviteFriend(userId, email.trim())
    setInviteMsg(res.ok ? `Invite sent to ${email.trim()}.` : res.error || 'Could not send invite.')
    if (res.ok) setEmail('')
    setTimeout(() => setInviteMsg(null), 4000)
  }

  const accept = async (r: FriendRequest) => {
    await acceptFriend(r.friendship_id, userId)
    refresh()
  }

  return (
    <div className="absolute inset-0 z-50 flex flex-col bg-background">
      <header className="flex items-center gap-2 h-16 px-4 border-b border-border shrink-0 bg-card">
        <Trophy className="size-5 text-warning-text" />
        <div className="flex-1 leading-tight">
          <h1 className="text-base font-semibold text-foreground">Friends</h1>
          <p className="text-[11px] text-muted-foreground">Compare streaks and coins — never health data</p>
        </div>
        <button onClick={onClose} className="rounded-md p-1.5 hover:bg-muted" aria-label="Close"><X className="size-5 text-foreground" /></button>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-lg px-4 py-5 space-y-5">
          {/* Invite */}
          <div className="rounded-xl border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">Add a friend</h2>
            <div className="mt-2 flex gap-2">
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitInvite()}
                placeholder="their email"
                type="email"
                className="flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground"
              />
              <Button variant="primary" size="sm" className="gap-1.5 shrink-0" onClick={submitInvite}>
                <UserPlus className="size-4" /> Invite
              </Button>
            </div>
            {inviteMsg && <p className="mt-2 text-xs text-muted-foreground">{inviteMsg}</p>}
          </div>

          {/* Pending requests */}
          {requests.length > 0 && (
            <div>
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Requests</h2>
              <div className="space-y-2">
                {requests.map((r) => (
                  <div key={r.friendship_id} className="flex items-center justify-between gap-2 rounded-lg border border-border-subtle bg-card px-3 py-2.5">
                    <span className="text-sm text-foreground">{r.name} wants to be friends</span>
                    <Button size="sm" variant="basic" className="gap-1 shrink-0" onClick={() => accept(r)}>
                      <Check className="size-3.5" /> Accept
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Leaderboard</h2>
            {loading ? (
              <div className="space-y-2">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-14 rounded-lg skeleton" />)}</div>
            ) : entries.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Invite a friend to start a leaderboard.</p>
            ) : (
              <div className="space-y-2">
                {entries.map((e) => (
                  <div
                    key={e.user_id}
                    className={cn('flex items-center gap-3 rounded-xl border px-3 py-3', e.you ? 'border-primary bg-accent' : 'border-border bg-card')}
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-foreground">
                      {e.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-foreground truncate">{e.name}{e.you && ' (you)'}</div>
                      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        <Flame className="size-3" /> {e.streak}-day streak · {e.level}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="text-sm font-semibold text-foreground">{e.coins.toLocaleString()}</div>
                      <div className="text-[10px] text-muted-foreground">coins</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
